<div class="camera-scanner-overlay">

  <!-- ── Header ──────────────────────────────────────────────────────────── -->
  <div class="scanner-header">
    <span class="scanner-title">Tag Plate Scanner</span>
    <div class="header-badges">
      @if (modelLoading) {
        <span class="badge badge-loading">
          <mat-spinner diameter="14"></mat-spinner> Loading AI…
        </span>
      } @else if (modelAvailable) {
        <span class="badge badge-ai" [title]="'Model: ' + detector.modelVersion">AI {{ detector.modelVersion }}</span>
      } @else {
        <span class="badge badge-manual">Manual</span>
      }
    </div>
    <button mat-icon-button (click)="close()" aria-label="Close scanner">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- ── Error banner ────────────────────────────────────────────────────── -->
  @if (error) {
    <div class="scanner-error">
      <mat-icon>error_outline</mat-icon>
      <span>{{ error }}</span>
    </div>
  }

  <!-- ── Viewport: video + canvas + overlay ──────────────────────────────── -->
  <div class="scanner-viewport">
    <video #video autoplay playsinline muted [class.hidden]="frozen"></video>
    <canvas #canvas [class.visible]="frozen"></canvas>
    <!-- Transparent overlay for bounding boxes (always on top) -->
    <canvas #overlayCanvas class="overlay-canvas"
            [class.drawable]="mode === 'manual-draw' || mode === 'annotate'"
            (pointerdown)="onOverlayPointerDown($event)"
            (pointermove)="onOverlayPointerMove($event)"
            (pointerup)="onOverlayPointerUp()"></canvas>

    <!-- Live detection count badge -->
    @if (!frozen && liveDetections.length > 0) {
      <div class="live-detection-badge">
        <mat-icon>center_focus_strong</mat-icon>
        {{ liveDetections.length }} plate(s) detected
      </div>
    }

    @if (scanning) {
      <div class="scanning-overlay">
        <mat-spinner diameter="48"></mat-spinner>
        <span>{{ scanStatus || 'Scanning…' }}</span>
      </div>
    }
  </div>

  <!-- ── Manual draw instructions ────────────────────────────────────────── -->
  @if (mode === 'manual-draw') {
    <div class="instruction-bar">
      <mat-icon>touch_app</mat-icon>
      <span>Draw a rectangle around each tag plate, then tap <strong>Scan</strong></span>
      @if (drawnBoxes.length > 0) {
        <span class="box-count">{{ drawnBoxes.length }} box(es)</span>
      }
    </div>
  }

  @if (mode === 'annotate') {
    <div class="instruction-bar annotate">
      <mat-icon>model_training</mat-icon>
      <span>Draw boxes around <strong>all visible tag plates</strong> to train the AI model</span>
      @if (drawnBoxes.length > 0) {
        <span class="box-count">{{ drawnBoxes.length }} box(es)</span>
      }
    </div>
  }

  <!-- ── Focus-region chips ──────────────────────────────────────────────── -->
  @if (frozen && focusRegions.length > 0 && mode !== 'manual-draw' && mode !== 'annotate') {
    <div class="focus-regions-bar">
      <span class="focus-regions-title">Detected regions</span>
      <div class="focus-regions-chips">
        @for (region of focusRegions; track region.label + region.bbox.x0) {
          <span class="region-chip" [style.border-color]="region.color" [style.color]="region.color">
            <span class="region-dot" [style.background]="region.color"></span>
            {{ region.label }}
          </span>
        }
      </div>
    </div>
  }

  <!-- ── OCR results ─────────────────────────────────────────────────────── -->
  @if (frozen && detectedLines.length > 0) {
    <div class="detected-lines-panel">
      <div class="lines-header">
        <span>Detected text lines ({{ detectedLines.length }})</span>
        <button mat-button (click)="selectAll()">
          {{ allSelected ? 'Deselect All' : 'Select All' }}
        </button>
      </div>
      <div class="lines-list">
        @for (line of detectedLines; track line.text + line.bbox.x0) {
          <div class="line-item" role="button" tabindex="0" [class.selected]="line.selected" (click)="toggleLine(line)" (keydown.enter)="toggleLine(line)" (keydown.space)="toggleLine(line)">
            <mat-checkbox [checked]="line.selected" (click)="$event.stopPropagation()" (change)="toggleLine(line)">
            </mat-checkbox>
            <span class="line-text">{{ line.text }}</span>
          </div>
        }
      </div>
    </div>
  }

  @if (frozen && detectedLines.length === 0 && !scanning && mode === 'results') {
    <div class="no-text-found">
      <mat-icon>info_outline</mat-icon>
      <span>No text detected. Try retaking with better lighting, adjust the detected area, or enter the tag number manually.</span>
      <div class="no-text-actions">
        <button mat-stroked-button (click)="refineAndTrain()" *ngIf="focusRegions.length > 0">
          <mat-icon>model_training</mat-icon>
          Adjust Area &amp; Train AI
        </button>
      </div>
      <div class="manual-tag-input">
        <mat-form-field appearance="outline">
          <mat-label>Tag number</mat-label>
          <input matInput [(ngModel)]="manualTagNumber" placeholder="e.g. 20-PA-001"
                 (keyup.enter)="searchManualTag()">
        </mat-form-field>
        <button mat-raised-button color="primary" (click)="searchManualTag()" [disabled]="!manualTagNumber.trim()">
          <mat-icon>search</mat-icon>
          Search
        </button>
      </div>
    </div>
  }

  <!-- ── Debug panel ─────────────────────────────────────────────────────── -->
  @if (frozen && debugImages.length > 0) {
    <div class="debug-toggle-bar">
      <button mat-button (click)="toggleDebug()">
        <mat-icon>bug_report</mat-icon>
        {{ showDebug ? 'Hide' : 'Show' }} Debug ({{ debugImages.length }})
      </button>
    </div>
    @if (showDebug) {
      <div class="debug-panel">
        @for (img of debugImages; track img.label) {
          <div class="debug-image-card">
            <span class="debug-image-label">{{ img.label }}</span>
            <img [src]="img.url" [alt]="img.label" />
          </div>
        }
      </div>
    }
  }

  <!-- Hidden file input -->
  <input #fileInput type="file" accept="image/*" (change)="onFileSelected($event)" style="display:none">

  <!-- ── Action bar ──────────────────────────────────────────────────────── -->
  <div class="scanner-actions">
    @if (mode === 'idle') {
      <button mat-raised-button color="primary" (click)="scan()" [disabled]="scanning || !!error">
        <mat-icon>camera</mat-icon>
        Capture &amp; Detect
      </button>
      <button mat-stroked-button (click)="openFilePicker()">
        <mat-icon>image</mat-icon>
        Load Image
      </button>
    }

    @if (mode === 'manual-draw') {
      <button mat-stroked-button (click)="undoLastBox()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>undo</mat-icon>
        Undo
      </button>
      <button mat-stroked-button (click)="clearDrawnBoxes()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>clear_all</mat-icon>
        Clear
      </button>
      <button mat-raised-button color="primary" (click)="confirmManualBoxes()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>search</mat-icon>
        Scan ({{ drawnBoxes.length }})
      </button>
      <button mat-stroked-button (click)="enterAnnotateMode()">
        <mat-icon>model_training</mat-icon>
        Train AI
      </button>
    }

    @if (mode === 'annotate') {
      <button mat-stroked-button (click)="undoLastBox()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>undo</mat-icon>
        Undo
      </button>
      <button mat-stroked-button (click)="clearDrawnBoxes()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>clear_all</mat-icon>
        Clear
      </button>
      <button mat-raised-button color="accent" (click)="submitAnnotations()" [disabled]="drawnBoxes.length === 0">
        <mat-icon>cloud_upload</mat-icon>
        Submit &amp; Scan ({{ drawnBoxes.length }})
      </button>
    }

    @if (mode === 'results') {
      <div class="manual-tag-input-row">
        <mat-form-field appearance="outline">
          <mat-label>Add tag manually</mat-label>
          <input matInput [(ngModel)]="manualTagNumber" placeholder="e.g. 20-PA-001"
                 (keyup.enter)="addManualTag()">
        </mat-form-field>
        <button mat-icon-button color="primary" (click)="addManualTag()" [disabled]="!manualTagNumber.trim()"
                matTooltip="Add to list">
          <mat-icon>add_circle</mat-icon>
        </button>
      </div>
    }

    @if (mode === 'results') {
      <button mat-raised-button (click)="retake()">
        <mat-icon>replay</mat-icon>
        Retake
      </button>
      <button mat-stroked-button (click)="refineAndTrain()" *ngIf="focusRegions.length > 0">
        <mat-icon>model_training</mat-icon>
        Adjust &amp; Train
      </button>
      <button mat-raised-button color="primary" (click)="confirmSelection()" [disabled]="!hasSelection">
        <mat-icon>search</mat-icon>
        Search Selected
      </button>
    }

    @if (mode === 'detecting' || mode === 'ocr-running') {
      <!-- Actions disabled while processing -->
    }
  </div>
</div>
